import {
  ChangeEvent,
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { IconCaretDown, IconQuestionMark } from '@tabler/icons-react'
import { Input, InputRef, PopoverProps } from 'antd'
import { search } from 'fast-fuzzy'
import { Dictionary } from 'lodash'
import Image from 'next/image'
import styled, { css } from 'styled-components'

import { Popover } from '../shared-components/BUI/Popover'
import { Text } from '../shared-components/BUI/Text'
import { getProjectColour, ProjectIcon } from '../shared-components/ProjectIcon'

import { accountManagers } from '@/config/constants/roleGroups'
import { useFilters } from '@/context/filterContext'
import {
  BUILDER_PROJECT_STATUS,
  StoredProject,
  useGroupedProjects,
  useProject,
} from '@/context/projectContext'
import { useUser } from '@/context/userContext'
import { useRoles } from '@/utils/ensureRole'
import { convertEnumToString } from '@/utils/index'

import { buildPassTheme } from 'src/styles/theme'

import { ProjectStatus } from 'types/api.graphql'

const Box = styled.div`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  color: ${({ theme }) => theme.colour.secondary._300};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  border: 1px solid ${({ theme }) => theme.colour.neutral.border};
  margin-right: 0.375rem;
  display: flex;
  justify-content: center;
  align-items: center;
`

const ProjectSelectorText = styled.span`
  ${({ theme }) => theme.font.label02};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const NoProject = styled(ProjectSelectorText)`
  flex-grow: 1;
  text-align: left;
  color: ${({ theme }) => theme.colour.secondary._400};
  display: flex;
  flex-direction: column;
`

const ProjectAndBuilder = styled.span`
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

const SelectedProjectName = styled(ProjectSelectorText)`
  display: block;
  color: ${({ theme }) => theme.colour.secondary.base};
  font-weight: 600;
  text-align: left;
`

const BuilderName = styled(ProjectSelectorText)`
  ${({ theme }) => theme.font.caption01}
  color: ${({ theme }) => theme.colour.secondary._400};
  text-align: left;
`

const Carret = styled.div`
  height: 14px;
  text-align: right;
  transform: rotate(0deg);
  transition: 0.2s;
`

const ProjectSelectorButton = styled.button`
  width: 100%;
  padding: 0;
  padding-right: 0.5rem;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background: transparent;
  display: flex;
  align-items: center;
  gap: 0.125rem;
  cursor: pointer;

  &:hover {
    outline: 1px solid ${({ theme }) => theme.colour.neutral.border};

    & ${Box} {
      border-color: transparent;
      color: ${({ theme }) => theme.colour.secondary.base};
    }

    & ${NoProject} {
      color: ${({ theme }) => theme.colour.secondary.base};
    }

    & ${Carret} {
      transform: translateY(4px);
    }
  }
`

const ProjectsWrapper = styled.div`
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
`

const Search = styled(Input).attrs({
  size: 'middle',
  placeholder: 'Search',
})`
  font-family: 'geist';
  width: 100%;
  margin-bottom: 0.5rem;
  border: none;
  border: 1px solid ${({ theme }) => theme.colour.neutral.border};
  border-radius: ${({ theme }) => theme.borderRadius.small};

  &:hover,
  &:focus {
    box-shadow: none;
    border-color: ${({ theme }) => theme.colour.secondary._100} !important;
  }
`

const Status = styled.span`
  position: sticky;
  top: 0;
  ${({ theme }) => theme.font.caption01};
  font-weight: 400;
  color: ${({ theme }) => theme.colour.secondary._400};
  background-color: ${({ theme }) => theme.colour.neutral.white};
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  margin-top: 1.25rem;
  z-index: 1;

  &:first-child {
    margin-top: 0;
  }
`

const ProjectBox = styled.div`
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  border: 1px solid ${({ theme }) => theme.colour.neutral.border};
  display: flex;
  justify-content: center;
  align-items: center;
`

const ProjectName = styled.span`
  ${({ theme }) => theme.font.label01};
  font-weight: 500;
  color: ${({ theme }) => theme.colour.secondary._800};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

type ProjectRowProps = {
  active?: boolean
}

const ProjectRow = styled.button<ProjectRowProps>`
  padding: 0.25rem 0.5rem;
  background-color: transparent;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  gap: 0.625rem;
  align-items: center;

  &:hover {
    background-color: ${({ theme }) => theme.colour.neutral.surface};
  }

  ${({ active, theme }) =>
    active &&
    css`
      background-color: ${theme.colour.semantic.successLight};

      &:hover {
        background-color: ${theme.colour.semantic.successLight};
      }

      & ${ProjectName} {
        color: ${theme.colour.semantic.successDark};
      }
    `}
`

const SEARCH_THRESHOLD = 7
const STATUSES = [
  ProjectStatus.InProgress,
  ProjectStatus.Upcoming,
  ProjectStatus.Complete,
]

export const BuilderLogo: React.FC = () => {
  const { builder } = useUser()
  const logo = builder?.logoUrl
  const name = builder?.company?.name

  return logo ? (
    <Image
      src={logo}
      width={32}
      height={32}
      alt={name}
      style={{ objectFit: 'contain' }}
    />
  ) : (
    <span>{name ? name[0].toUpperCase() : 'Your organization'}</span>
  )
}

type ProjectsProps = {
  allProjects: StoredProject[]
  groupedProjects: Dictionary<StoredProject[]>
  selectProject: (project: StoredProject) => void
  showBuilderProject: boolean
  selectectProjectId?: string
}

const Projects: React.FC<ProjectsProps> = ({
  allProjects,
  groupedProjects,
  selectProject,
  selectectProjectId,
  showBuilderProject = true,
}) => {
  const { hasRole } = useRoles()
  const wrapperRef = useRef<HTMLDivElement>(undefined)
  const inputRef = useRef<InputRef>(null)
  const [matchedProjectNames, setMatchedProjectNames] = useState<string[]>(null)

  const builderProject = groupedProjects[BUILDER_PROJECT_STATUS][0]
  const isAccountManager = hasRole(accountManagers)

  const allProjectProjects = allProjects.filter(
    (p) => p.status !== BUILDER_PROJECT_STATUS
  )

  const allProjectNames = useMemo(() => {
    return allProjectProjects.map((project) => project.name)
  }, [allProjectProjects])

  const renderBuilderProject =
    showBuilderProject && isAccountManager && matchedProjectNames === null

  useEffect(() => {
    wrapperRef.current?.focus() // make projects tabbable on open
    inputRef.current?.focus() // focus search input on open
  }, [])

  const onSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    if (!value) {
      setMatchedProjectNames(null)
    } else {
      setMatchedProjectNames(
        search(e.target.value, allProjectNames, {
          threshold: 0.9, // decided by James & Aaron the last day of the 2022 year
        })
      )
    }
  }

  const renderProjectRow = (project: StoredProject) => {
    const isBuilder = project.status === BUILDER_PROJECT_STATUS
    const background = getProjectColour(project)

    return (
      <ProjectRow
        key={project.id}
        tabIndex={0}
        onClick={() => selectProject(project)}
        active={selectectProjectId === project.id}
        data-tag={`project-select-option${isBuilder ? '-all' : ''}`}
      >
        <ProjectBox>
          {isBuilder ? <BuilderLogo /> : <ProjectIcon colour={background} />}
        </ProjectBox>
        <ProjectName>{project.name}</ProjectName>
      </ProjectRow>
    )
  }

  return (
    <ProjectsWrapper ref={wrapperRef} tabIndex={-1}>
      {allProjectProjects?.length >= SEARCH_THRESHOLD && (
        <Search onChange={onSearch} ref={inputRef} />
      )}

      {renderBuilderProject && renderProjectRow(builderProject)}

      {matchedProjectNames === null
        ? STATUSES.filter((status) => !!groupedProjects[status]).map(
            (statusEnum) => (
              <Fragment key={statusEnum}>
                <Status>{convertEnumToString(statusEnum, false)}</Status>
                {groupedProjects[statusEnum].map(renderProjectRow)}
              </Fragment>
            )
          )
        : allProjectProjects
            .filter((p) => matchedProjectNames.includes(p.name))
            .map(renderProjectRow)}
    </ProjectsWrapper>
  )
}

type Props = {
  showBuilderProject?: boolean
  getPopupContainer?: PopoverProps['getPopupContainer']
}

export const ProjectSelector: React.FC<Props> = ({
  showBuilderProject,
  getPopupContainer,
}) => {
  const { builder } = useUser()
  const { updateFilters } = useFilters()
  const { projects, project, projectsLoading, updateProject } = useProject()
  const [groupedProjects, activeProjects] = useGroupedProjects()
  const [isOpen, setIsOpen] = useState(false)

  const selectProject = (project: StoredProject) => {
    updateProject(project || null)
    updateFilters({}) // reset pagination
    setIsOpen(false)
  }

  useEffect(() => {
    const onGlobalKeydown = (e: KeyboardEvent) => {
      if (e.key === 'g' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setIsOpen((prev) => !prev)
      }
    }

    document.addEventListener('keydown', onGlobalKeydown)
    return () => {
      document.removeEventListener('keydown', onGlobalKeydown)
    }
  }, [])

  const selectedProject = useMemo(() => {
    if (!project) return null

    const selectedProject = projects.find((p) => p.id === project.id)

    if (!selectedProject && projectsLoading) return project // optimistically show the project from LocalStorage if we're still loading projects...

    return selectedProject
  }, [project, projects, projectsLoading])

  const selectedProjectIcon = useMemo(() => {
    if (!selectedProject) {
      return <IconQuestionMark size={22} />
    } else if (selectedProject.status === BUILDER_PROJECT_STATUS) {
      return <BuilderLogo />
    }
    return <ProjectIcon colour={getProjectColour(selectedProject)} />
  }, [selectedProject])

  return (
    <Popover
      open={isOpen}
      onOpenChange={setIsOpen}
      placement="bottomLeft"
      showArrow={false}
      overlayStyle={{ minWidth: '300px' }}
      getPopupContainer={getPopupContainer}
      destroyTooltipOnHide
      content={
        <>
          <Projects
            allProjects={activeProjects}
            groupedProjects={groupedProjects}
            selectectProjectId={selectedProject?.id}
            showBuilderProject={showBuilderProject}
            selectProject={selectProject}
          />
          {projectsLoading && (
            <Text
              align="center"
              variant="caption01"
              block
              mt="1rem"
              mb="1rem"
              secondary="_500"
            >
              Loading more projects...
            </Text>
          )}
        </>
      }
    >
      <ProjectSelectorButton data-tag="project-select">
        <Box>{selectedProjectIcon}</Box>
        {selectedProject ? (
          <ProjectAndBuilder>
            <BuilderName title={builder?.company?.name}>
              {builder?.company?.name}
            </BuilderName>
            <SelectedProjectName title={selectedProject.name}>
              {selectedProject.name}
            </SelectedProjectName>
          </ProjectAndBuilder>
        ) : (
          <NoProject>
            <BuilderName title={builder?.company?.name}>
              {builder?.company?.name}
            </BuilderName>
            Select a project
          </NoProject>
        )}
        <Carret>
          <IconCaretDown
            size={14}
            fill={buildPassTheme.colour.secondary.base}
          />
        </Carret>
      </ProjectSelectorButton>
    </Popover>
  )
}
